import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Paper, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Box, Tabs, Tab, Avatar } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useAuthStore } from '../store/authStore';
import { useThemeContext } from '../components/contexts/ThemeContext';
import { CustomAlert } from '../components/CustomAlert';
import api from '../security/AxiosConfig';
import { AgGridReact } from 'ag-grid-react';
import * as MyCollections from "../services/MyCollections";
import Controls from '../components/controls/Controls';
import UploadCorrectionDialog from '../components/UploadCorrectionDialog';
import * as CommonFunctions from '../services/CommonFunctions';
import { logActivity } from '../services/ApiServices';

const useStyles = makeStyles((theme) => ({
  pageContent: {
    marginTop: 20,
    padding: '24px',
    borderRadius: '12px',
  },
  headerTitle: {
    fontWeight: 'bold',
    fontSize: '20px',
    color: '#0f2c59',
    marginBottom: '20px',
    textTransform: 'uppercase',
  },
  releaseBtn: {
    border: '1px solid #2ecc71',
    color: '#2ecc71',
    backgroundColor: 'transparent',
    padding: '6px 16px',
    borderRadius: '20px',
    fontWeight: 'bold',
    fontSize: '11px',
    cursor: 'pointer',
    textTransform: 'uppercase',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#2ecc71',
      color: '#ffffff',
    },
  },
  uploadBtn: {
    border: '1px solid #d35400',
    color: '#ffffff',
    backgroundColor: '#e67e22',
    padding: '6px 16px',
    borderRadius: '20px',
    fontWeight: 'bold',
    fontSize: '11px',
    cursor: 'pointer',
    textTransform: 'uppercase',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
    '&:hover': {
      backgroundColor: '#d35400',
      borderColor: '#d35400',
      boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
    },
    '&:disabled': {
      backgroundColor: '#27ae60',
      borderColor: '#1e8449',
      color: '#ffffff',
      cursor: 'not-allowed',
      boxShadow: 'none',
      pointerEvents: 'none',
      opacity: 0.85,
    },
  },
}));


const AccountantAvatarRenderer = (props) => {
  const fullName = props.value || '';
  const parts = fullName.trim().split(' ');
  const surname = parts[0] || '';
  const name = parts[1] || '';
  const initials = `${surname.charAt(0) || ''}${name.charAt(0) || ''}`;

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%', gap: '10px', padding: '4px 0', justifyContent: 'flex-start' }}>
      <Avatar
        sx={{
          width: 24,
          height: 24,
          fontSize: '11px',
          bgcolor: props.context?.darkTheme ? '#feb33c' : '#ffe599',
          color: 'black',
          fontWeight: 'bold'
        }}
      >
        {initials}
      </Avatar>
      <span style={{ fontWeight: '600', color: props.context?.darkTheme ? 'white' : 'inherit' }}>{fullName}</span>
    </div>
  );
};

function SalaryBlocks() {
  const { roles } = useAuthStore();

  const contactId = useAuthStore().contactId;
  const token = useAuthStore().token;
  const userName = useAuthStore().fullName;
  const classes = useStyles();
  const { mode } = useThemeContext();
  const darkTheme = mode === 'dark';

  const [blocks, setBlocks] = useState([]);

  const isSupervisorAccountant = roles?.includes('SUPERVISOR_ACCOUNTANT');
  const isChiefAccountant = roles?.includes('CHIEF_ACCOUNTANT');
  const canToggleView = isSupervisorAccountant || isChiefAccountant;
  const [viewMode, setViewMode] = useState(canToggleView ? 'ACCOUNTANT' : null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [tabValue, setTabValue] = useState('active');

  const isAdminOrSupervisor =
    roles?.includes('ADMIN') ||
    roles?.includes('SUPERVISOR') ||
    roles?.includes('ADMIN_SUPERVISOR') ||
    roles?.includes('SUPERVISOR_ACCOUNTANT') ||
    roles?.includes('MANAGER');

  const fetchBlocks = useCallback((mode = viewMode) => {
    const url = mode ? `/salaryBlocks?viewMode=${mode}` : '/salaryBlocks';
    api.get(url)
      .then((res) => {
        setBlocks(res.data);
      })
      .catch((err) => {
        console.error('Error fetching salary blocks:', err);
      });
  }, [viewMode]);

  useEffect(() => {
    fetchBlocks(viewMode);
  }, [viewMode, fetchBlocks]);

  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [selectedSettlementId, setSelectedSettlementId] = useState(null);

  const handleOpenUploadDialog = useCallback((settlementId) => {
    if (!settlementId) {
      CustomAlert("Rozliczenie nie zostało powiązane z tą blokadą.", "Błąd", "error");
      return;
    }
    setSelectedSettlementId(settlementId);
    setOpenUploadDialog(true);
  }, []);

  const handleUploadCorrection = useCallback((file) => {
    setOpenUploadDialog(false);
    CommonFunctions.handleFileSubmit(
      file,
      selectedSettlementId,
      81, // fixedFileType is 81 (Correction of monthly statement / salary block)
      MyCollections.fileAssignmentId.ACCOUNTING_SETTLEMENT,
      token
    ).then(response => {
      if (response && response.success) {
        CustomAlert("Faktura korygująca została pomyślnie wgrana.", "Sukces", "success");
        const block = blocks.find(b => b.accountingSettlementId === selectedSettlementId);
        const targetAccountantName = block ? block.accountantName : `Rozliczenie ID: ${selectedSettlementId}`;
        const periodStr = block ? `${String(block.statementMonth).padStart(2, '0')}.${block.statementYear}` : '';
        logActivity(
          `${MyCollections.logActionType.SALARY_BLOCK_CORRECTION_UPLOAD}${targetAccountantName}, okres: ${periodStr}, plik: ${file.name}`,
          userName,
          token,
          block ? Number(block.accountantId) : null,
          block ? Number(block.statementMonth) : null,
          block ? Number(block.statementYear) : null,
          true
        );
        fetchBlocks(viewMode);
      }
    });
  }, [selectedSettlementId, token, fetchBlocks, viewMode, blocks, userName]);


  const handleOpenConfirm = (id) => {
    setSelectedBlockId(id);
    setConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    setSelectedBlockId(null);
    setConfirmOpen(false);
  };

  const handleReleaseBlock = () => {
    if (!selectedBlockId) return;

    const block = blocks.find(b => b.id === selectedBlockId);
    const targetAccountantName = block ? block.accountantName : `Blokada ID: ${selectedBlockId}`;
    const periodStr = block ? `${String(block.statementMonth).padStart(2, '0')}.${block.statementYear}` : '';
    const amountStr = block ? `${block.amount} PLN` : '';

    api.post(`/salaryBlocks/${selectedBlockId}/release`)
      .then(() => {
        CustomAlert('Blokada została pomyślnie zdjęta. Zwrot zostanie dopisany do najbliższego otwartego zestawienia.', 'Sukces', 'success');
        logActivity(
          `${MyCollections.logActionType.SALARY_BLOCK_RELEASE}${targetAccountantName}, kwota: ${amountStr}, okres: ${periodStr}`,
          userName,
          token,
          block ? Number(block.accountantId) : null,
          block ? Number(block.statementMonth) : null,
          block ? Number(block.statementYear) : null,
          true
        );
        fetchBlocks();
        handleCloseConfirm();
      })
      .catch((err) => {
        console.error('Error releasing block:', err);
        CustomAlert('Wystąpił błąd podczas zdejmowania blokady.', 'Błąd', 'error');
        handleCloseConfirm();
      });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const isManagingRole = roles?.includes('ADMIN') ||
    roles?.includes('SUPERVISOR') ||
    roles?.includes('ADMIN_SUPERVISOR') ||
    roles?.includes('SUPERVISOR_ACCOUNTANT') ||
    roles?.includes('CHIEF_ACCOUNTANT') ||
    roles?.includes('MANAGER') ||
    roles?.includes('ACCOUNT_MANAGER');

  const shouldShowOwnOnly = viewMode === 'ACCOUNTANT' || !isManagingRole;

  const activeBlocks = blocks
    .filter((b) => b.status === 'ACTIVE')
    .filter((b) => {
      if (shouldShowOwnOnly) {
        return Number(b.accountantId) === Number(contactId);
      }
      return true;
    });

  const historyBlocks = blocks
    .filter((b) => b.status !== 'ACTIVE')
    .filter((b) => {
      if (shouldShowOwnOnly) {
        return Number(b.accountantId) === Number(contactId);
      }
      return true;
    });

  const formatPeriod = (month, year) => {
    if (!month || !year) return '';
    return `Zestawienie ${String(month).padStart(2, '0')}.${year}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pl-PL');
  };

  const calculateDaysBetween = (start, end) => {
    if (!start || !end) return '';
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // AG Grid Default Column Definitions
  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: true,
    floatingFilter: false,
    flex: 1,
    minWidth: 120,
  }), []);

  // AG Grid Active Blocks Column Definitions
  const activeColumnDefs = useMemo(() => [
    {
      headerName: "Księgowa",
      field: "accountantName",
      filter: 'agTextColumnFilter',
      cellRenderer: AccountantAvatarRenderer
    },
    {
      headerName: "Kwota blokady",
      field: "amount",
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => params.value ? `${params.value.toFixed(2)} PLN` : '',
      cellStyle: { fontWeight: 'bold', display: 'flex', alignItems: 'center' }
    },
    {
      headerName: "Kto nałożył?",
      field: "createdByName",
      filter: 'agTextColumnFilter',
      cellStyle: { display: 'flex', alignItems: 'center' }
    },
    {
      headerName: "Za jaki okres?",
      valueGetter: (params) => formatPeriod(params.data?.statementMonth, params.data?.statementYear),
      filter: 'agTextColumnFilter',
      cellStyle: { display: 'flex', alignItems: 'center' }
    },
    {
      headerName: "Nałożona w dniu",
      field: "createdAt",
      filter: 'agDateColumnFilter',
      valueFormatter: (params) => formatDate(params.value),
      cellStyle: { display: 'flex', alignItems: 'center' }
    },
    {
      headerName: "Warunek zdjęcia blokady",
      field: "description",
      filter: 'agTextColumnFilter',
      flex: 2,
      wrapText: true,
      autoHeight: true,
      cellStyle: { display: 'flex', alignItems: 'center', whiteSpace: 'pre-wrap', lineHeight: '1.5' }
    },
    {
      headerName: "Akcja",
      width: 150,
      minWidth: 150,
      maxWidth: 160,
      sortable: false,
      filter: false,
      floatingFilter: false,
      cellRenderer: (params) => {
        const isOwnBlock = roles?.includes('SUPERVISOR_ACCOUNTANT') && Number(params.data?.accountantId) === Number(contactId);
        if (shouldShowOwnOnly) {
          const settlementId = params.data?.accountingSettlementId;
          // Backend now returns isCorrectionUploaded directly in SalaryBlockDTO
          const correctionUploaded = params.data?.isCorrectionUploaded;
          return (
            <button
              className={classes.uploadBtn}
              onClick={() => handleOpenUploadDialog(settlementId)}
              disabled={correctionUploaded}
              title={correctionUploaded ? 'Korekta została już wgrana' : 'Wgraj fakturę korygującą'}
            >
              {correctionUploaded ? '✓ Wgrano korektę' : 'Wgraj korektę'}
            </button>
          );
        }
        if (!isAdminOrSupervisor || isOwnBlock) return null;
        return (
          <button
            className={classes.releaseBtn}
            onClick={() => handleOpenConfirm(params.data?.id)}
          >
            Zdejmij blokadę
          </button>
        );
      },
      cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' }
    }
  ], [darkTheme, isAdminOrSupervisor, classes.releaseBtn, classes.uploadBtn, contactId, roles, shouldShowOwnOnly, handleOpenUploadDialog]);

  // AG Grid History Blocks Column Definitions
  const historyColumnDefs = useMemo(() => [
    {
      headerName: "Księgowa",
      field: "accountantName",
      filter: 'agTextColumnFilter',
      cellRenderer: AccountantAvatarRenderer
    },
    {
      headerName: "Kwota blokady",
      field: "amount",
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => params.value ? `${params.value.toFixed(2)} PLN` : '',
      cellStyle: { display: 'flex', alignItems: 'center' }
    },
    {
      headerName: "Osoba nakładająca",
      field: "createdByName",
      filter: 'agTextColumnFilter',
      cellStyle: { display: 'flex', alignItems: 'center' }
    },
    {
      headerName: "Okres",
      valueGetter: (params) => formatPeriod(params.data?.statementMonth, params.data?.statementYear),
      filter: 'agTextColumnFilter',
      cellStyle: { display: 'flex', alignItems: 'center' }
    },
    {
      headerName: "Okres zwrotu",
      valueGetter: (params) => formatPeriod(params.data?.returnStatementMonth, params.data?.returnStatementYear),
      filter: 'agTextColumnFilter',
      cellStyle: { display: 'flex', alignItems: 'center' }
    },
    {
      headerName: "Nałożona w dniu",
      field: "createdAt",
      filter: 'agDateColumnFilter',
      valueFormatter: (params) => formatDate(params.value),
      cellStyle: { display: 'flex', alignItems: 'center' }
    },
    {
      headerName: "Warunek zdjęcia",
      field: "description",
      filter: 'agTextColumnFilter',
      flex: 2,
      wrapText: true,
      autoHeight: true,
      cellStyle: { display: 'flex', alignItems: 'center', whiteSpace: 'pre-wrap', lineHeight: '1.5' }
    },
    {
      headerName: "Data zdjęcia",
      field: "releasedAt",
      filter: 'agDateColumnFilter',
      valueGetter: (params) => {
        const days = calculateDaysBetween(params.data?.createdAt, params.data?.releasedAt);
        const formattedDate = formatDate(params.data?.releasedAt);
        return formattedDate ? `${formattedDate} (zdjęta po ${days} dniach)` : '';
      },
      cellStyle: { display: 'flex', alignItems: 'center' }
    },
    {
      headerName: "Osoba zdejmująca",
      field: "releasedByName",
      filter: 'agTextColumnFilter',
      cellStyle: { display: 'flex', alignItems: 'center' }
    },
    {
      headerName: "Akcja",
      width: 150,
      minWidth: 150,
      maxWidth: 160,
      sortable: false,
      filter: false,
      floatingFilter: false,
      hide: !shouldShowOwnOnly,
      cellRenderer: (params) => {
        if (!shouldShowOwnOnly) return null;
        const settlementId = params.data?.accountingSettlementId;
        // Backend now returns isCorrectionUploaded directly in SalaryBlockDTO
        const correctionUploaded = params.data?.isCorrectionUploaded;
        return (
          <button
            className={classes.uploadBtn}
            onClick={() => handleOpenUploadDialog(settlementId)}
            disabled={correctionUploaded}
            title={correctionUploaded ? 'Korekta została już wgrana' : 'Wgraj fakturę korygującą'}
          >
            {correctionUploaded ? '✓ Wgrano korektę' : 'Wgraj korektę'}
          </button>
        );
      },
      cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' }
    }
  ], [shouldShowOwnOnly, classes.uploadBtn, handleOpenUploadDialog]);

  return (
    <>
      <Paper
        className={classes.pageContent}
        elevation={0}
        style={{ backgroundColor: darkTheme ? '#1e1e1e' : '#ffffff', color: darkTheme ? '#ffffff' : '#333333' }}
      >
        {canToggleView && (
          <Box display="flex" gap="10px" sx={{ px: 2, pt: 1, pb: 2 }}>
            <Controls.PremiumButton
              variant={viewMode === 'ACCOUNTANT' ? 'blue' : 'glass'}
              size="small"
              onClick={() => setViewMode('ACCOUNTANT')}
              label="Widok Księgowego"
              icon="fas fa-user-tie"
            />
            {isChiefAccountant && (
              <Controls.PremiumButton
                variant={viewMode === 'CHIEF' ? 'blue' : 'glass'}
                size="small"
                onClick={() => setViewMode('CHIEF')}
                label="Widok Głównego Księgowego"
                icon="fas fa-user-shield"
              />
            )}
            {isSupervisorAccountant && !isChiefAccountant && (
              <Controls.PremiumButton
                variant={viewMode === 'SUPERVISOR' ? 'blue' : 'glass'}
                size="small"
                onClick={() => setViewMode('SUPERVISOR')}
                label="Widok Superwizora"
                icon="fas fa-user-shield"
              />
            )}
          </Box>
        )}
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 2, pt: 1, mb: 3, borderBottom: 1, borderColor: darkTheme ? '#333' : '#e0e0e0' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
            aria-label="salary blocks navigation tabs"
            sx={{
              "& .MuiTabs-indicator": {
                backgroundColor: darkTheme ? "#90caf9" : "#1976d2",
                height: "3px",
                borderRadius: "2px",
                transition: "all 0.3s ease-in-out",
              },
            }}
          >
            <Tab
              value="active"
              label="Aktywne blokady"
              sx={{
                fontSize: "14px",
                textTransform: "none",
                color: darkTheme ? "#fff" : "#555",
                padding: "12px 20px",
                borderRadius: "8px 8px 0 0",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  backgroundColor: darkTheme ? "#333" : "#f5f5f5",
                  color: darkTheme ? "#90caf9" : "#1976d2",
                },
                "&.Mui-selected": {
                  color: darkTheme ? "#90caf9" : "#1976d2",
                  backgroundColor: darkTheme ? "#252525" : "#e3f2fd",
                  fontWeight: 'bold'
                },
              }}
            />
            <Tab
              value="history"
              label="Historia blokad"
              sx={{
                fontSize: "14px",
                textTransform: "none",
                color: darkTheme ? "#fff" : "#555",
                padding: "12px 20px",
                borderRadius: "8px 8px 0 0",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  backgroundColor: darkTheme ? "#333" : "#f5f5f5",
                  color: darkTheme ? "#90caf9" : "#1976d2",
                },
                "&.Mui-selected": {
                  color: darkTheme ? "#90caf9" : "#1976d2",
                  backgroundColor: darkTheme ? "#252525" : "#e3f2fd",
                  fontWeight: 'bold'
                },
              }}
            />
          </Tabs>
        </Box>

        {tabValue === 'active' ? (
          <div
            className={darkTheme ? 'ag-theme-balham-dark' : 'ag-theme-balham'}
            style={{ height: 550, width: '100%' }}
          >
            <AgGridReact
              rowData={activeBlocks}
              columnDefs={activeColumnDefs}
              defaultColDef={defaultColDef}
              animateRows={true}
              localeText={MyCollections.localeTextPL}
              pagination={true}
              paginationPageSize={20}
              suppressCellFocus={true}
            />
          </div>
        ) : (
          <div
            className={darkTheme ? 'ag-theme-balham-dark' : 'ag-theme-balham'}
            style={{ height: 550, width: '100%' }}
          >
            <AgGridReact
              rowData={historyBlocks}
              columnDefs={historyColumnDefs}
              defaultColDef={defaultColDef}
              animateRows={true}
              localeText={MyCollections.localeTextPL}
              pagination={true}
              paginationPageSize={20}
              suppressCellFocus={true}
            />
          </div>
        )}
      </Paper>

      <Dialog
        open={confirmOpen}
        onClose={handleCloseConfirm}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        PaperProps={{
          style: {
            backgroundColor: darkTheme ? '#1e1e1e' : '#ffffff',
            color: darkTheme ? '#ffffff' : '#333333',
          },
        }}
      >
        <DialogTitle id="confirm-dialog-title" style={{ fontWeight: 'bold' }}>
          Zdejmij blokadę wynagrodzenia
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description" style={{ color: darkTheme ? '#cccccc' : '#666666' }}>
            Czy na pewno chcesz zdjąć wybraną blokadę wynagrodzenia? Kwota blokady zostanie dodana jako zwrot w najbliższym otwartym okresie rozliczeniowym tej księgowej.
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button onClick={handleCloseConfirm} style={{ color: '#888888', fontWeight: 'bold' }}>
            Zrezygnuj
          </Button>
          <Button onClick={handleReleaseBlock} style={{ color: '#2ecc71', fontWeight: 'bold' }} autoFocus>
            Potwierdź
          </Button>
        </DialogActions>
      </Dialog>

      <UploadCorrectionDialog
        open={openUploadDialog}
        handleClose={() => setOpenUploadDialog(false)}
        handleUpload={handleUploadCorrection}
        fixedFileType={81}
      />
    </>
  );
}

export default SalaryBlocks;
